<template>
    <b-sidebar
      id="sidebar"
      :visible="isSidebarActive"
      bg-variant="white"
      sidebar-class="sidebar-lg"
      shadow
      backdrop
      no-header
      no-close-on-backdrop
      right
      @shown="loadData"
      @hidden="onClose"
      @change="(val) => $emit('update:is-sidebar-active', val)"
    >
      <template #default="{ hide }">
        <!-- Header -->
        <div
          class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
        >
          <h5 class="mb-0 text-capitalize">
            {{
              (isAdd ? $t("actions.add") : $t("actions.edit")) +
              " " +
              $tc("hanwha_dvr.title", 1)
            }}
          </h5>

          <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
        </div>

        <!-- BODY -->
        <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
          <!-- Form -->
          <b-form
            class="p-2"
            @submit.prevent="handleSubmit(onSubmit)"
            @reset.prevent="onClose"
          >
            <validation-provider
              #default="validationContext"
              name="tenant"
              rules="required"
            >
              <b-form-group
                label="Tenant"
                label-for="tenant_id"
                :state="getValidationState(validationContext)"
              >
                <v-select
                  v-model="itemData.id_tenant"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="tenantOptions"
                  :clearable="false"
                  :reduce="(val) => val.value"
                  input-id="tenant_id"
                  @input="onChangeTenant"
                />
                <b-form-invalid-feedback :state="getValidationState(validationContext)">
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <b-form-group v-if="$can('read', 'HanwhaAlarmProfile')" :label="$tc('hanwha_alarm_profile.title', 1)" label-for="id_hanwha_alarm_profile">
              <v-select
                v-model="itemData.id_hanwha_alarm_profile"
                :options="alarmProfileOptions"
                :reduce="val => val.value"
                :clearable="true"
                input-id="id_hanwha_alarm_profile"
                :disabled="!itemData.id_tenant"
              />
            </b-form-group>

            <!-- Name -->
            <validation-provider #default="validationContext" name="name" rules="required">
              <b-form-group
                class="text-capitalize"
                :label="$t('hanwha_dvr.name')"
                label-for="name"
              >
                <b-form-input
                  id="name"
                  v-model="itemData.name"
                  :state="getValidationState(validationContext)"
                  trim
                />

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Unit -->
            <validation-provider #default="validationContext" name="Facility" rules="required">
              <b-form-group
                class="text-capitalize"
                :label="$tc('unit.title', 1)"
                label-for="id_unit"
                :state="getValidationState(validationContext)"
              >
                <units-select-paginated
                  :value="itemData.id_unit"
                  :initial-options="selectedUnitOptions"
                  :multiple="false"
                  id="id_unit"
                  append-to-body
                  :calculate-position="withPopper"
                  @input="onChangeUnit"
                />
                <b-form-invalid-feedback :state="getValidationState(validationContext)">
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Additional router -->
            <validation-provider #default="validationContext" :name="$t('hanwha_dvr.router')" >
              <b-form-group
                class="text-capitalize"
                :label="$t('hanwha_dvr.router')"
                label-for="id_additional_router"
                :state="getValidationState(validationContext)"
              >
                <v-select
                  :clereable="false"
                  :reduce="(val) => val.id"
                  :options="additionalRouters"
                  input-id="id_additional_router"
                  :get-option-label="(option) => `IP ${option.ip_vpn}`"
                  v-model="itemData.id_additional_router"
                  :disabled="!itemData.id_unit || isLoadingRouters || routersLoadFailed"
                  append-to-body
                  :calculate-position="withPopper"
                >
                  <template #option="{ ip_vpn }">
                    <span>IP {{ ip_vpn ? ip_vpn : "N/A" }}</span>
                  </template>
                </v-select>
                <b-form-invalid-feedback :state="getValidationState(validationContext)">
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- ip / host -->
            <host-ip-input
              v-model="itemData.ip"
              :label="$t('hanwha_dvr.ip')"
              :show-port="false"
              class="mb-1"
            />

            <!-- user -->
            <validation-provider #default="validationContext" name="user" rules="required">
              <b-form-group
                class="text-capitalize"
                :label="$tc('hanwha_dvr.user', 1)"
                label-for="user"
              >
                <b-form-input
                  id="user"
                  v-model="itemData.user"
                  :state="getValidationState(validationContext)"
                  trim
                />

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Password -->
            <validation-provider
              #default="validationContext"
              name="password"
              rules="required"
            >
              <label class="d-block" for="password">
                {{ $t('hanwha_dvr.password') }}
              </label>
              <b-input-group
                class="text-capitalize"
                :label="$t('hanwha_dvr.password')"
                label-for="password"
              >
                <b-form-input
                  id="password"
                  v-model="itemData.password"
                  :state="getValidationState(validationContext)"
                  trim
                  :type="passwordFieldType"
                />

                <b-input-group-append>
                  <b-input-group-text
                    style="border-right: 1px solid #d8d6de !important;"
                    class="cursor-pointer"
                    @click="togglePasswordVisibility"
                  >
                    <feather-icon
                      color="#000"
                      :icon="passwordToggleIcon"
                    />
                  </b-input-group-text>
                </b-input-group-append>

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-input-group>
            </validation-provider>

            <!-- http schema -->
            <validation-provider
              #default="validationContext"
              rules="required"
              :name="$t('hanwha_dvr.http_schema')"
              class="d-block mt-2"
            >
              <b-form-group
                :label="$t('hanwha_dvr.http_schema')"
                label-for="http_schema"
                :state="getValidationState(validationContext)"
              >
                <v-select
                  v-model="itemData.http_schema"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="httpSchemaOptions"
                  :clearable="false"
                  input-id="http_schema"
                />
                <b-form-invalid-feedback :state="getValidationState(validationContext)">
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- http port -->
            <validation-provider
              #default="validationContext"
              rules="required"
              :name="$t('hanwha_dvr.http_port')"
            >
              <b-form-group
                class="text-capitalize"
                :label="$t('hanwha_dvr.http_port')"
                label-for="http_port"
              >
                <b-form-input
                  id="http_port"
                  v-model="itemData.http_port"
                  :state="getValidationState(validationContext)"
                  trim
                />

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- rtsp schema -->
            <validation-provider
              #default="validationContext"
              rules="required"
              :name="$t('hanwha_dvr.rtsp_schema')"
            >
              <b-form-group
                :label="$t('hanwha_dvr.rtsp_schema')"
                label-for="rtsp_schema"
                :state="getValidationState(validationContext)"
              >
                <v-select
                  v-model="itemData.rtsp_schema"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="rtspSchemaOptions"
                  :clearable="false"
                  input-id="rtsp_schema"
                />
                <b-form-invalid-feedback :state="getValidationState(validationContext)">
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- rtsp port -->
            <validation-provider
              #default="validationContext"
              rules="required"
              :name="$t('hanwha_dvr.rtsp_port')"
            >
              <b-form-group
                class="text-capitalize"
                :label="$t('hanwha_dvr.rtsp_port')"
                label-for="rtsp_port"
              >
                <b-form-input
                  id="rtsp_port"
                  v-model="itemData.rtsp_port"
                  :state="getValidationState(validationContext)"
                  trim
                />

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- enabled_recording_audit -->
            <b-form-group class="mt-2">
              <b-form-checkbox
                v-model="itemData.enabled_recording_audit"
                switch
              >
                {{ $t('hanwha_dvr.enabled_recording_audit') }}
              </b-form-checkbox>
            </b-form-group>

            <!-- days_recording_audit -->
            <validation-provider
              v-if="itemData.enabled_recording_audit"
              #default="validationContext"
              rules="required|positive"
              :name="$t('hanwha_dvr.days_recording_audit')"
            >
              <b-form-group
                :label="$t('hanwha_dvr.days_recording_audit')"
                label-for="days_recording_audit"
              >
                <b-form-input
                  id="days_recording_audit"
                  v-model.number="itemData.days_recording_audit"
                  type="number"
                  :state="getValidationState(validationContext)"
                  min="1"
                />

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <b-form-group
              :label="$t('hanwha_dvr.auditStartAt')"
              label-for="audit_start_at"
            >
              <flat-pickr
                id="audit_start_at"
                v-model="itemData.audit_start_at"
                class="timeNoBackground w-100"
                :config="datePickerConfig"
                :input-attributes="{ autocomplete: 'off' }"
              />
            </b-form-group>

            <!-- Form Actions -->
            <div class="d-flex mt-2">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                class="mr-2 text-capitalize"
                type="submit"
                :disabled="isLoadingRouters || routersLoadFailed"
              >
                {{ isAdd ? $t("actions.add") : $t("actions.edit") }}
              </b-button>
              <b-button
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="button"
                variant="outline-secondary"
                @click="onClose(hide)"
                class="text-capitalize"
              >
                {{ $t("actions.cancel") }}
              </b-button>
            </div>
          </b-form>
        </validation-observer>
      </template>
    </b-sidebar>
  </template>

  <script>
  import {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    BFormCheckbox,
    BInputGroupAppend,
  } from "bootstrap-vue";
  import { ValidationProvider, ValidationObserver } from "vee-validate";
  import vSelect from "vue-select";
  import { computed, ref, onBeforeUnmount } from "@vue/composition-api";
  import { required, alphaNum, email, positive, required_if } from "@validations";
  import { useToast } from "vue-toastification/composition";
  import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
  import formValidation from "@core/comp-functions/forms/form-validation";
  import Ripple from "vue-ripple-directive";
  import store from "@/store";
  import HostIpInput from "@/components/HostIpInput.vue";
  import UnitsSelectPaginated from "@/components/UnitsSelectPaginated.vue";
  import popperHelper from "@/libs/popper-helper";
  import axios from "@axios";
  import ability from '@/libs/acl/ability';
  import flatPickr from "vue-flatpickr-component";
  import { Spanish } from "flatpickr/dist/l10n/es.js";

  export default {
    components: {
      UnitsSelectPaginated,
      BSidebar,
      BForm,
      BFormGroup,
      BFormInput,
      BFormInvalidFeedback,
      BInputGroupAppend,
      BButton,
      vSelect,
      HostIpInput,
      BFormCheckbox,
      flatPickr,

      // Form Validation
      ValidationProvider,
      ValidationObserver,
    },
    directives: {
      Ripple,
    },
    model: {
      prop: "isSidebarActive",
      event: "update:is-sidebar-active",
    },
    props: {
      isSidebarActive: {
        type: Boolean,
        required: true,
      },
      itemEdit: {
        type: Object,
        required: false,
      },
      tenantOptions: {
        type: Array,
        required: true,
      },
      unitOptions: {
        type: Array,
        required: true,
      },
      isAdd: {
        type: Boolean,
        required: true,
      },
    },
    data() {
      return {
        required,
        alphaNum,
        email,
        positive,
      };
    },
    setup(props, { emit, root }) {
      const blankData = {
        name: "",
        description: "",
        user: "",
        password: "",
        ip: "",
        id_tenant: null,
        id_hanwha_alarm_profile: null,
        http_port: null,
        rtsp_port: null,
        http_schema: "http",
        rtsp_schema: "rtsp",
        enabled_recording_audit: false,
        days_recording_audit: 0,
        audit_start_at: null
      };

      const additionalRouters = ref([]);
      const isLoadingRouters = ref(false);
      const routersLoadFailed = ref(false);
      const selectedUnit = ref([]);
      const selectedUnitOptions = computed(() => [...props.unitOptions, ...selectedUnit.value]);
      let routerRequestId = 0;
      onBeforeUnmount(() => { routerRequestId += 1; });
      const alarmProfileOptions = computed(() => store.getters['hanwha_alarm_profiles/getSelectedItems']
        .filter(profile => profile.id_tenant === itemData.value.id_tenant && profile.enabled));

      const passwordFieldType = ref("password");
      const passwordToggleIcon = computed(() => {
        return passwordFieldType.value === "password" ? "EyeIcon" : "EyeOffIcon";
      });
      const httpSchemaOptions = ["http", "https", "tcp"];
      const rtspSchemaOptions = ["rtsp", "rtsps", "tcp"];

      const togglePasswordVisibility = () => {
        passwordFieldType.value =
        passwordFieldType.value === "password" ? "text" : "password";
      };

      const datePickerConfig = computed(() => ({
        minDate: props.isAdd ? new Date() : null,
        dateFormat: "Y-m-d",
        allowInput: false,
        locale: {
          firstDayOfWeek: 1,
          ...Spanish
        },
      }));

      const toast = useToast();

      const itemData = ref(JSON.parse(JSON.stringify(blankData)));
      const normalizeAuditStartAt = value => value
        ? String(value).slice(0, 10)
        : null;

      const setItemData = item => {
        itemData.value = JSON.parse(JSON.stringify(item));
        itemData.value.audit_start_at = normalizeAuditStartAt(item.audit_start_at);
      };

      const resetData = () => {
        routerRequestId += 1;
        additionalRouters.value = [];
        selectedUnit.value = [];
        isLoadingRouters.value = false;
        routersLoadFailed.value = false;
        itemData.value = JSON.parse(JSON.stringify(blankData));
      };

      const onSubmit = () => {
        if (isLoadingRouters.value || routersLoadFailed.value) return;
        let dispatch;

        if (props.isAdd) {
          dispatch = store.dispatch("hanwha_dvr/add", itemData.value);
        } else {
          const req = {
            id: itemData.value.id,
            params: itemData.value,
          };

          dispatch = store.dispatch("hanwha_dvr/edit", req);
        }

        dispatch
          .then((response) => {
            if (response.success) {
              toast({
                component: ToastificationContent,
                props: {
                  title: response.message,
                  icon: "CheckIcon",
                  variant: "success",
                },
              });
              emit("refetch-data");
              emit("update:is-sidebar-active", false);
            } else {
              toast({
                component: ToastificationContent,
                props: {
                  title: response.message,
                  icon: "AlertTriangleIcon",
                  variant: "danger",
                },
              });
            }
          })
          .catch((response) => {
            toast({
              component: ToastificationContent,
              props: {
                title: response.response.data.message,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          });
      };

      const { refFormObserver, getValidationState, resetForm } = formValidation(resetData);

      const loadData = () => {
        resetData();
        if (ability.can('read', 'HanwhaAlarmProfile')) {
          store.dispatch('hanwha_alarm_profiles/getAll', { max: 1000, enabled: true });
        }
        additionalRouters.value = [];

        if (!props.isAdd) {
          setItemData(props.itemEdit);
          itemData.value.id_additional_router = props.itemEdit.id_additional_router ?? props.itemEdit.camera_proxy_router?.camera?.id ?? null;

          additionalRouters.value = props.itemEdit.additional_routers_all || [];
        }

        if(props.isAdd && props.itemEdit?.from_url) {
          setItemData({ ...itemData.value, ...props.itemEdit });
        }
        itemData.value.id_unit = itemData.value.id_unit == null ? null : Number(itemData.value.id_unit);
        loadUnitRouters(itemData.value.id_additional_router);
      };

      const onClose = (hide) => {
        if(hide) hide();
        console.log("onClose")
        resetData()
        resetForm()
      };

      const loadUnitRouters = async (selectedRouter = null) => {
        const requestId = ++routerRequestId;
        additionalRouters.value = [];
        selectedUnit.value = [];
        itemData.value.id_additional_router = null;
        routersLoadFailed.value = false;
        const idUnit = itemData.value.id_unit;
        isLoadingRouters.value = !!idUnit;
        if (!idUnit) return;
        try {
          const { data } = await axios.get(`/v1/unit/${idUnit}`, { params: { withCameras: false } });
          if (requestId !== routerRequestId) return;
          const unit = data.data;
          selectedUnit.value = [{ value: Number(unit.id), label: unit.description }];
          additionalRouters.value = (unit.additional_routers_all || []).map(router => ({ ...router, id: Number(router.id) }));
          itemData.value.id_additional_router = additionalRouters.value.find(router => String(router.id) === String(selectedRouter))?.id ?? null;
        } catch (error) {
          if (requestId !== routerRequestId) return;
          routersLoadFailed.value = true;
          toast({
            component: ToastificationContent,
            props: { title: error.response?.data?.message || root.$t("messages.error.server_error"), icon: "AlertTriangleIcon", variant: "danger" },
          });
        } finally {
          if (requestId === routerRequestId) isLoadingRouters.value = false;
        }
      };

      const onChangeUnit = (idUnit) => {
        const normalizedId = idUnit == null ? null : Number(idUnit);
        if (normalizedId === itemData.value.id_unit && !routersLoadFailed.value) return;
        itemData.value.id_unit = normalizedId;
        loadUnitRouters();
      };

      const onChangeTenant = () => {
        const selectedProfile = alarmProfileOptions.value.find(profile => profile.value === itemData.value.id_hanwha_alarm_profile);
        if (!selectedProfile) itemData.value.id_hanwha_alarm_profile = null;
      };

      return {
        selectedUnitOptions,
        isLoadingRouters,
        routersLoadFailed,
        withPopper: popperHelper.withPopper,
        itemData,
        onSubmit,

        refFormObserver,
        getValidationState,
        loadData,
        onClose,

        passwordFieldType,
        passwordToggleIcon,
        httpSchemaOptions,
        rtspSchemaOptions,
        togglePasswordVisibility,
        datePickerConfig,

        onChangeUnit,
        onChangeTenant,
        additionalRouters,
        alarmProfileOptions,
      };
    },
  };
  </script>

<style scoped>
.timeNoBackground {
  border-radius: 0.3em;
  background-color: transparent;
  margin: 0;
  border-radius: 0.3em;
  border: 1px solid #cecece;
  background-color: transparent;
  padding: 0.75em;
  margin: 0;
}
</style>
